import { useEffect, useState } from "react";
import { Loader2, Send, ShieldCheck, TriangleAlert, CheckCircle2, MessageSquare } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * SmsHealthCard: super admin only card that shows whether Twilio env
 * vars are wired and offers a one-click test-send so the operator can
 * validate credentials without creating a fake shift.
 */
export default function SmsHealthCard() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/sms/health");
      setHealth(data);
    } catch (e) {
      // Non super admin will 403: silently hide the card in that case.
      setHealth(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="rounded-2xl glass-card p-5">
        <div className="flex items-center gap-2 text-white/60 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading SMS health
        </div>
      </div>
    );
  }
  if (!health) return null;

  const sendTest = async () => {
    if (!testTo.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/sms/test", { to: testTo.trim() });
      if (data.ok) toast.success(`SMS sent to ${data.sent_to} (${data.live ? "live Twilio" : "stub"})`);
      else toast.error(`SMS ${data.live ? "failed at Twilio" : "dev logged only, live keys not set"} for ${data.sent_to}. Check logs.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Test send failed.");
    } finally {
      setSending(false);
    }
  };

  const envKey = (name, present) => (
    <div className="flex items-center justify-between text-xs tabular">
      <span className="text-white/50">{name}</span>
      {present ? (
        <span className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 className="w-3 h-3" /> set</span>
      ) : (
        <span className="inline-flex items-center gap-1 text-amber-300"><TriangleAlert className="w-3 h-3" /> missing</span>
      )}
    </div>
  );

  return (
    <div className="rounded-2xl glass-card p-5 space-y-4" data-testid="sms-health-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-amber-200" />
          <div>
            <div className="label-eyebrow">Roster SMS notifications</div>
            <div className="font-display text-lg mt-1">
              Mode:{" "}
              <span className={health.live ? "text-emerald-300" : "text-amber-300"}>
                {health.live ? "Live Twilio" : "Dev stub"}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-3 text-[11px] tabular text-white/60">
          <div><span className="text-emerald-300">{health.counts?.sent || 0}</span> sent</div>
          <div><span className="text-rose-300">{health.counts?.failed || 0}</span> failed</div>
          <div><span className="text-amber-300">{health.counts?.dev_logged || 0}</span> dev</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-xl border border-white/10 bg-white/[0.03] p-3">
        {envKey("TWILIO_ACCOUNT_SID", health.env_present?.TWILIO_ACCOUNT_SID)}
        {envKey("TWILIO_AUTH_TOKEN", health.env_present?.TWILIO_AUTH_TOKEN)}
        {envKey("TWILIO_FROM_NUMBER", health.env_present?.TWILIO_FROM_NUMBER)}
        {envKey("TWILIO_MESSAGING_SERVICE_SID", health.env_present?.TWILIO_MESSAGING_SERVICE_SID)}
      </div>

      {!health.live && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-100 space-y-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            <div className="font-medium">SMS is running in stub mode. Roster alerts are only being dev logged.</div>
          </div>
          <ol className="list-decimal ml-5 space-y-1 text-amber-100/90">
            <li>Create a Twilio account at twilio.com and top up trial credit.</li>
            <li>Grab your <strong>Account SID</strong> and <strong>Auth Token</strong> from the Twilio Console home page.</li>
            <li>Either buy a phone number (Phone Numbers, Buy a Number) and copy it as <strong>TWILIO_FROM_NUMBER</strong> in E.164 form, or create a Messaging Service and copy its SID as <strong>TWILIO_MESSAGING_SERVICE_SID</strong>. Prefer the Messaging Service option because it auto scales and picks the best sender per destination.</li>
            <li>Paste all three (or two) values into your production backend env vars, then redeploy. This card will flip to Live Twilio automatically.</li>
            <li>For US traffic you must complete A2P 10DLC brand + campaign registration first. For Australia buy an Australian long code or use the alphanumeric Sender ID. For UK use a UK long code.</li>
          </ol>
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-xs text-white/60">Send a test SMS</Label>
        <div className="flex gap-2">
          <Input
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="Your mobile in E.164 or local format"
            data-testid="sms-test-to"
          />
          <Button
            onClick={sendTest}
            disabled={sending || !testTo.trim()}
            data-testid="sms-test-send"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            <span className="ml-1.5">Send test</span>
          </Button>
        </div>
        <div className="text-[11px] text-white/40">
          Trial accounts can only send to numbers verified in the Twilio Console.
        </div>
      </div>

      {(health.recent || []).length > 0 && (
        <div className="space-y-1.5">
          <div className="label-eyebrow">Recent outbox</div>
          <div className="rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full text-xs">
              <tbody>
                {health.recent.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 last:border-b-0">
                    <td className="px-3 py-2 tabular text-white/60">{(r.created_at || "").slice(11, 16)}</td>
                    <td className="px-3 py-2">
                      <span className={
                        r.status === "sent" ? "text-emerald-300" :
                        r.status === "failed" ? "text-rose-300" :
                        "text-amber-300"
                      }>{r.status}</span>
                    </td>
                    <td className="px-3 py-2 tabular">{r.to}</td>
                    <td className="px-3 py-2 text-white/60 truncate max-w-[280px]">{r.body}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
